import { createClient } from "@supabase/supabase-js";
import { env } from "./env";
import { platform } from "@/platform";

/**
 * Cliente del backend. En Windows/Android la sesión se guarda en el almacenamiento de la app
 * (no en localStorage del webview) para que sobreviva a limpiezas del navegador embebido.
 */
export const supabase = createClient(env.supabaseUrl, env.supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    // En /auth/app-callback el código es de la app de Windows/Android (se canjea allá, no acá).
    // 1.105.0: en el catálogo (/c/…) la vuelta de Google es de «Mi cuenta» del cliente (su propio cliente, abajo).
    detectSessionInUrl: env.platform === "web" && !(typeof window !== "undefined" && (window.location.pathname === "/auth/app-callback" || window.location.pathname.startsWith("/c/"))),
    // Delegación en cada llamada: la plataforma real se carga en initPlatform() después de este módulo.
    storage: {
      getItem: (k) => platform.authStorage.getItem(k),
      setItem: (k, v) => platform.authStorage.setItem(k, v),
      removeItem: (k) => platform.authStorage.removeItem(k),
    },
    flowType: "pkce",
  },
});

/**
 * Cuando una edge function responde con error (400, 403, 500…), supabase-js devuelve solo
 * "Edge Function returned a non-2xx status code" y deja el motivo real en la respuesta.
 * Acá se lee esa respuesta una vez para todas las llamadas: `error.message` pasa a ser el mensaje
 * de la función (por ejemplo, el de ARCA) y `data` trae el cuerpo, así `data?.error` también funciona.
 */
export async function readFunctionError(error: unknown): Promise<{ message: string; body: unknown } | null> {
  const ctx = (error as { context?: unknown } | null)?.context;
  if (!(ctx instanceof Response)) return null;
  try {
    const text = await ctx.clone().text();
    try {
      const body = JSON.parse(text) as { error?: unknown; message?: unknown; msg?: unknown; detail?: unknown };
      const msg = body.error ?? body.message ?? body.msg;
      const detail = typeof body.detail === "string" && body.detail ? ` (${body.detail})` : "";
      return { message: typeof msg === "string" && msg ? msg + detail : text || `Error ${ctx.status}`, body };
    } catch {
      return { message: text || `Error ${ctx.status}`, body: null };
    }
  } catch {
    return null;
  }
}

const statusOf = (error: unknown) => { const ctx = (error as { context?: unknown } | null)?.context; return ctx instanceof Response ? ctx.status : null; };

/** Renueva la sesión si vence en menos de un minuto (sin internet no hace nada). */
export async function freshSession() {
  try {
    const { data } = await supabase.auth.getSession();
    const exp = data.session?.expires_at;
    if (data.session && exp && exp * 1000 < Date.now() + 60_000) await supabase.auth.refreshSession();
  } catch { /* sin internet: la llamada va a fallar igual y se reintenta */ }
}

/** Pide volver a poner la contraseña (ReauthDialog) sin cerrar la sesión ni borrar los datos del equipo. */
export const REAUTH_EVENT = "sofia:reauth";
export function requestReauth(reason?: string) { try { window.dispatchEvent(new CustomEvent(REAUTH_EVENT, { detail: reason ?? null })); } catch { /* fuera del navegador */ } }

// supabase.functions crea un cliente nuevo en cada acceso: se parchea el prototipo compartido (una sola vez).
type Invoke = typeof supabase.functions.invoke;
const proto = Object.getPrototypeOf(supabase.functions) as { invoke: Invoke; __sofiaPatched?: boolean };
if (!proto.__sofiaPatched) {
  const originalInvoke = proto.invoke;
  proto.invoke = async function (this: unknown, ...args: Parameters<Invoke>) {
    const call = () => (originalInvoke as (...a: Parameters<Invoke>) => ReturnType<Invoke>).apply(this, args);
    // La sesión de internet puede vencer mientras el equipo sigue abierto con el PIN: se renueva antes.
    await freshSession();
    let res = await call();
    if (res.error && statusOf(res.error) === 401) {
      // Token vencido o revocado: se renueva y se prueba una vez más; si no se puede, se pide la contraseña.
      const { data } = await supabase.auth.refreshSession();
      if (data.session) res = await call();
      if (!data.session || (res.error && statusOf(res.error) === 401)) requestReauth(res.error ? (await readFunctionError(res.error))?.message : undefined);
    }
    if (res.error) {
      const detail = await readFunctionError(res.error);
      if (detail) {
        res.error.message = detail.message;
        return { data: detail.body ?? { error: detail.message }, error: res.error } as Awaited<ReturnType<Invoke>>;
      }
    }
    return res;
  } as Invoke;
  proto.__sofiaPatched = true;
}
